import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, FlatList, TouchableOpacity, Platform, Pressable, Modal, ActivityIndicator } from 'react-native';
import WebView from 'react-native-webview';
import { FontAwesome5 } from '@expo/vector-icons';
import { getTwilioMessage, getEmailLogs, getEmailLogById, retryEmailLog, sendEmail } from '../../../api/Reservation';
import { TextMediumSize, TextSmallSize, TextdefaultSize } from '../../../common/constants/Fonts';
import { useAlertModal } from '../../../common/hooks';

interface Props {
  reservationInfo?: any;
}

const CommunicationTab = ({ reservationInfo }: Props) => {
  const [messages, setMessages] = useState<any[]>([]);
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);
  const { showAlert } = useAlertModal();
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);

  const [subTab, setSubTab] = useState<'sms' | 'email'>('sms');
  const [emailLogs, setEmailLogs] = useState<any[]>([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [expandedEmailId, setExpandedEmailId] = useState<number | null>(null);

  // Track container width to make the email table responsive.
  const [containerWidth, setContainerWidth] = useState(0);

  // Email detail viewer (HTML content fetched on demand)
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<any | null>(null);
  const [loadingEmailDetail, setLoadingEmailDetail] = useState(false);

  // Tracks the id of the email currently being retried (disables its button).
  const [retryingEmailId, setRetryingEmailId] = useState<number | null>(null);

  // Disables the "Send Confirmation Email" button while a request is in flight.
  const [sendingConfirmation, setSendingConfirmation] = useState(false);

  useEffect(() => {
    getTwilioMessage({ phone_number: reservationInfo.phone_number }, (jsonRes, status, error) => {
      switch (status) {
        case 200:
          setMessages(jsonRes);
          break;
        default:
          setMessages([]);
          break;
      }
    });
  }, [reservationInfo]);

  const fetchEmailLogs = useCallback((showLoader = true) => {
    if (!reservationInfo?.id) {
      setEmailLogs([]);
      return;
    }
    if (showLoader) setLoadingEmails(true);
    getEmailLogs({ reservation_id: reservationInfo.id, limit: 100 }, (jsonRes, status, error) => {
      if (status === 200 && jsonRes?.data) {
        setEmailLogs(jsonRes.data);
      } else {
        setEmailLogs([]);
      }
      setLoadingEmails(false);
    });
  }, [reservationInfo?.id]);

  useEffect(() => {
    fetchEmailLogs();
  }, [fetchEmailLogs]);

  // Helper functions
  const getSentReceived = (direction: string) => {
    if (direction === "outbound-api") {
      return "Sent";
    } else if (direction === "outbound-reply") {
      return "Automatic Reply";
    } else {
      return "Received";
    }
  };

  const formatPhoneNumber = (phoneNumber: string) => {
    if (!phoneNumber) return 'N/A';

    var cleaned = ("" + phoneNumber).replace(/\D/g, "");
    var match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      var intlCode = match[1] ? "+1 " : "";
      return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join(
        ""
      );
    }
    return null;
  };

  const formatDate = (timestamp: string) => {
    try {
      return new Date(timestamp).toLocaleDateString('en-US');
    } catch {
      return timestamp;
    }
  };

  const formatTime = (timestamp: string) => {
    try {
      return new Date(timestamp).toLocaleTimeString('en-US');
    } catch {
      return timestamp;
    }
  };

  const renderSection = (title: string, children: React.ReactNode, icon?: string) => (
    <View style={styles.section}>
      {/* <View style={styles.sectionHeader}>
        {icon && <FontAwesome5 name={icon} size={18} color="#333" style={styles.sectionIcon} />}
        <Text style={styles.sectionTitle}>{title}</Text>
      </View> */}
      <View style={styles.sectionContent}>
        {children}
      </View>
    </View>
  );

  // Function to copy text to clipboard
  const copyToClipboard = (text: string, messageId: string) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text)
        .then(() => {
          setCopiedMessageId(messageId);
          // Reset after 2 seconds
          setTimeout(() => {
            setCopiedMessageId(null);
          }, 2000);
        })
        .catch(err => {
          console.error('Failed to copy text: ', err);
          showAlert('error', 'Could not copy text to clipboard');
        });
    } else {
      console.error('Clipboard API not available');
      showAlert('error', 'Clipboard functionality not available on this device');
    }
  };

  const renderMessageRow = ({ item }: { item: any }) => {
    return (
      <View style={styles.messageRow}>
        {/* <View style={[styles.messageCell, styles.directionCell]}>
          <View style={[styles.directionBadge, item.direction === 'outbound-api' ? styles.sentBadge : styles.receivedBadge]}>
            <Text style={[styles.directionText, item.direction === 'outbound-api' ? styles.sentText : styles.receivedText]}>
              {getSentReceived(item.direction)}
            </Text>
          </View>
        </View> */}

        {/* <View style={[styles.messageCell, styles.fromCell]}>
          <Text style={styles.messageCellValue}>{formatPhoneNumber(item.from)}</Text>
        </View>
        
        <View style={[styles.messageCell, styles.toCell]}>
          <Text style={styles.messageCellValue}>{formatPhoneNumber(item.to)}</Text>
        </View> */}
        
        <View 
          style={[styles.messageCell, styles.messageContentCell]}
          {...(Platform.OS === 'web' && {
            onMouseEnter: () => setHoveredMessageId(item.sid),
            onMouseLeave: () => setHoveredMessageId(null),
          } as any)}
        >
          <View style={styles.messageContentWrapper}>
            <Text selectable={true} style={styles.messageCellValue}>{item.body}</Text>
            {hoveredMessageId === item.sid && (
              <TouchableOpacity 
                style={styles.copyButton} 
                onPress={() => copyToClipboard(item.body, item.sid)}
              >
                <FontAwesome5 
                  name={copiedMessageId === item.sid ? "check" : "copy"} 
                  size={16} 
                  color={copiedMessageId === item.sid ? "#28a745" : "#007bff"} 
                />
              </TouchableOpacity>
            )}
          </View>
        </View>

        <View style={[styles.messageCell, styles.photoCell]}>
          <Text style={styles.messageCellValue}>{item.numMedia}</Text>
        </View>
        
        <View style={[styles.messageCell, styles.timeCell]}>
          <Text style={styles.messageCellValue}>
            {formatDate(item.dateSent)}
          </Text>
          <Text style={styles.messageCellValue}>
            {formatTime(item.dateSent)}
          </Text>
        </View>
      </View>
    )
  }

  const formatMessageType = (type?: string) => {
    if (!type) return 'Email';
    return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const getMessageTypeIcon = (type?: string) => {
    const t = (type || '').toLowerCase();
    if (t.includes('confirmation')) return 'check-circle';
    if (t.includes('cancel')) return 'times-circle';
    if (t.includes('reminder')) return 'bell';
    if (t.includes('receipt') || t.includes('invoice') || t.includes('payment')) return 'file-invoice-dollar';
    if (t.includes('backup')) return 'exclamation-triangle';
    return 'envelope';
  };

  // Fetch full email (incl. body_html) on demand and open the viewer modal.
  const openEmailDetail = (item: any) => {
    setSelectedEmail(item);
    setEmailModalVisible(true);

    if (item?.body_html) {
      return; // already have full content
    }

    setLoadingEmailDetail(true);
    getEmailLogById(item.id, (jsonRes: any, status: number) => {
      if (status === 200 && jsonRes?.data) {
        setSelectedEmail(jsonRes.data);
      }
      setLoadingEmailDetail(false);
    });
  };

  const closeEmailDetail = () => {
    setEmailModalVisible(false);
    setSelectedEmail(null);
    setLoadingEmailDetail(false);
  };

  // Retry a previously failed email. The API records a fresh log entry for the
  // attempt, so we refresh the list afterwards to reflect the new status.
  const handleRetryEmail = (item: any) => {
    if (!item?.id || retryingEmailId === item.id) return;
    setRetryingEmailId(item.id);
    retryEmailLog(item.id, (jsonRes: any, status: number) => {
      setRetryingEmailId(null);
      if (status === 200 && jsonRes?.success) {
        showAlert('success', jsonRes.message || 'Email re-sent successfully');
        closeEmailDetail();
        fetchEmailLogs(false);
      } else {
        showAlert('error', jsonRes?.message || 'Failed to re-send email');
        fetchEmailLogs(false);
      }
    });
  };

  // Manually (re)send the reservation confirmation email. Uses the shared
  // /sendemail endpoint with type='confirmation' (email only, no SMS).
  const handleSendConfirmationEmail = () => {
    if (sendingConfirmation) return;
    const toEmail = reservationInfo?.email;
    if (!toEmail) {
      showAlert('error', 'No customer email address on this reservation');
      return;
    }
    setSendingConfirmation(true);
    sendEmail(
      {
        id: reservationInfo?.id,
        order_number: reservationInfo?.order_number,
        method: 'email',
        type: 'confirmation',
        to: toEmail,
      },
      (jsonRes: any, status: number) => {
        setSendingConfirmation(false);
        if (status === 200 && jsonRes?.success) {
          showAlert('success', jsonRes.message || 'Confirmation email sent successfully');
          fetchEmailLogs(false);
        } else {
          showAlert('error', jsonRes?.message || jsonRes?.error || 'Failed to send confirmation email');
        }
      },
    );
  };

  // Responsive breakpoints: hide lower-priority columns as the panel narrows.
  const showTypeColumn = containerWidth === 0 || containerWidth >= 620;
  const showToColumn = containerWidth === 0 || containerWidth >= 480;

  const renderEmailRow = (item: any) => {
    const isExpanded = expandedEmailId === item.id;
    const isFailed = item.status === 'failed';
    return (
      <View key={item.id} style={styles.emailRowWrapper}>
        <TouchableOpacity
          style={styles.messageRow}
          activeOpacity={0.7}
          onPress={() => setExpandedEmailId(isExpanded ? null : item.id)}
        >
          {showTypeColumn && (
            <View style={[styles.messageCell, styles.emailTypeCell]}>
              <Text style={styles.messageCellValue} numberOfLines={2}>{formatMessageType(item.message_type)}</Text>
            </View>
          )}
          {showToColumn && (
            <View style={[styles.messageCell, styles.emailToCell]}>
              <Text style={styles.messageCellValue} numberOfLines={1}>{item.to_email || 'N/A'}</Text>
            </View>
          )}
          <View style={[styles.messageCell, styles.emailSubjectCell]}>
            <Text style={styles.messageCellValue} numberOfLines={1}>{item.subject || '—'}</Text>
            {!showToColumn && (
              <Text style={styles.emailSubRowMeta} numberOfLines={1}>{item.to_email || 'N/A'}</Text>
            )}
          </View>
          <View style={[styles.messageCell, styles.emailStatusCell]}>
            <View style={[styles.directionBadge, isFailed ? styles.failedBadge : styles.sentBadge]}>
              <Text style={[styles.directionText, isFailed ? styles.failedText : styles.sentText]}>
                {isFailed ? 'Failed' : 'Sent'}
              </Text>
            </View>
          </View>
          <View style={[styles.messageCell, styles.emailDateCell]}>
            <Text style={styles.messageCellValue}>{formatDate(item.createdAt)}</Text>
            <Text style={styles.messageCellValue}>{formatTime(item.createdAt)}</Text>
          </View>
        </TouchableOpacity>
        {isExpanded && (
          <View style={styles.emailDetail}>
            {!showTypeColumn && (
              <Text style={styles.emailDetailLabel}>Type: <Text style={styles.emailDetailValue}>{formatMessageType(item.message_type)}</Text></Text>
            )}
            {!!item.from_email && (
              <Text style={styles.emailDetailLabel}>From: <Text style={styles.emailDetailValue}>{item.from_email}</Text></Text>
            )}
            {isFailed && !!item.error_message && (
              <Text style={styles.emailErrorText}>Error: {item.error_message}</Text>
            )}
            {!!item.body_text && (
              <Text selectable={true} style={styles.emailBodyText} numberOfLines={4}>{item.body_text}</Text>
            )}
            <View style={styles.emailActionRow}>
              <TouchableOpacity style={styles.viewEmailBtn} onPress={() => openEmailDetail(item)}>
                <FontAwesome5 name="external-link-alt" size={12} color="#009999" style={{ marginRight: 6 }} />
                <Text style={styles.viewEmailBtnText}>View full email</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.retryEmailBtn, retryingEmailId === item.id && styles.retryEmailBtnDisabled]}
                onPress={() => handleRetryEmail(item)}
                disabled={retryingEmailId === item.id}
              >
                {retryingEmailId === item.id ? (
                  <ActivityIndicator size="small" color="#fff" style={{ marginRight: 6 }} />
                ) : (
                  <FontAwesome5 name="redo" size={12} color="#fff" style={{ marginRight: 6 }} />
                )}
                <Text style={styles.retryEmailBtnText}>
                  {retryingEmailId === item.id ? 'Retrying…' : 'Retry send'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
      </View>
    );
  };

  const renderEmailToolbar = () => (
    <View style={styles.emailToolbar}>
      <TouchableOpacity
        style={[styles.sendConfirmationBtn, sendingConfirmation && styles.sendConfirmationBtnDisabled]}
        onPress={handleSendConfirmationEmail}
        disabled={sendingConfirmation}
        accessibilityRole="button"
        accessibilityLabel="Send confirmation email"
      >
        {sendingConfirmation ? (
          <ActivityIndicator size="small" color="#fff" style={{ marginRight: 8 }} />
        ) : (
          <FontAwesome5 name="paper-plane" size={13} color="#fff" style={{ marginRight: 8 }} />
        )}
        <Text style={styles.sendConfirmationBtnText}>
          {sendingConfirmation ? 'Sending…' : 'Send Confirmation Email'}
        </Text>
      </TouchableOpacity>
    </View>
  );

  const renderEmailTable = () => (
    <View
      style={styles.tableContainer}
      onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}
    >
      <View style={styles.tableHeader}>
        {showTypeColumn && <Text style={[styles.columnHeader, styles.emailTypeCell]}>Type</Text>}
        {showToColumn && <Text style={[styles.columnHeader, styles.emailToCell]}>To</Text>}
        <Text style={[styles.columnHeader, styles.emailSubjectCell]}>Subject</Text>
        <Text style={[styles.columnHeader, styles.emailStatusCell]}>Status</Text>
        <Text style={[styles.columnHeader, styles.emailDateCell]}>Date</Text>
      </View>
      {loadingEmails ? (
        <View style={styles.emptyState}>
          <ActivityIndicator size="large" color="#009999" />
          <Text style={styles.emptyStateText}>Loading email logs…</Text>
        </View>
      ) : emailLogs.length > 0 ? (
        <ScrollView style={styles.messageList} showsVerticalScrollIndicator={false}>
          {emailLogs.map((item) => renderEmailRow(item))}
        </ScrollView>
      ) : (
        <View style={styles.emptyState}>
          <FontAwesome5 name="envelope-open-text" size={48} color="#ccc" />
          <Text style={styles.emptyStateText}>No email logs found</Text>
        </View>
      )}
    </View>
  );

  const renderEmailHtmlBody = () => {
    if (!selectedEmail) return null;
    const html = selectedEmail.body_html;
    const text = selectedEmail.body_text;

    if (html) {
      if (Platform.OS === 'web') {
        return (
          <div
            style={{ padding: 12, width: '100%', boxSizing: 'border-box', overflowX: 'auto' }}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        );
      }
      return (
        <WebView
          originWhitelist={['*']}
          source={{ html }}
          style={styles.emailWebView}
        />
      );
    }

    if (text) {
      return (
        <ScrollView style={styles.emailModalTextWrap}>
          <Text selectable style={styles.emailBodyText}>{text}</Text>
        </ScrollView>
      );
    }

    return (
      <View style={styles.emptyState}>
        <FontAwesome5 name="inbox" size={40} color="#ccc" />
        <Text style={styles.emptyStateText}>No content available for this email</Text>
      </View>
    );
  };

  const renderEmailModal = () => {
    const isFailed = selectedEmail?.status === 'failed';
    return (
      <Modal
        visible={emailModalVisible}
        transparent
        animationType="none"
        onRequestClose={closeEmailDetail}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            {/* Header */}
            <View style={styles.modalHeader}>
              <View style={{ flex: 1, paddingRight: 12 }}>
                <Text style={styles.modalSubject} numberOfLines={2}>
                  {selectedEmail?.subject || '(No subject)'}
                </Text>
                <View style={styles.modalMetaRow}>
                  <View style={[styles.statusPill, isFailed ? styles.failedBadge : styles.sentBadge]}>
                    <Text style={[styles.directionText, isFailed ? styles.failedText : styles.sentText]}>
                      {isFailed ? 'Failed' : 'Sent'}
                    </Text>
                  </View>
                  <Text style={styles.modalMetaText}>{formatMessageType(selectedEmail?.message_type)}</Text>
                </View>
              </View>
              <TouchableOpacity style={styles.modalCloseBtn} onPress={closeEmailDetail}>
                <FontAwesome5 name="times" size={18} color="#666" />
              </TouchableOpacity>
            </View>

            {/* Recipient / meta info */}
            <View style={styles.modalInfoBar}>
              {!!selectedEmail?.from_email && (
                <Text style={styles.modalInfoLine} numberOfLines={1}>
                  <Text style={styles.modalInfoKey}>From: </Text>{selectedEmail.from_email}
                </Text>
              )}
              <Text style={styles.modalInfoLine} numberOfLines={1}>
                <Text style={styles.modalInfoKey}>To: </Text>{selectedEmail?.to_email || 'N/A'}
              </Text>
              <Text style={styles.modalInfoLine} numberOfLines={1}>
                <Text style={styles.modalInfoKey}>Date: </Text>
                {selectedEmail ? `${formatDate(selectedEmail.createdAt)} ${formatTime(selectedEmail.createdAt)}` : ''}
              </Text>
              {isFailed && !!selectedEmail?.error_message && (
                <Text style={styles.emailErrorText}>Error: {selectedEmail.error_message}</Text>
              )}
              <TouchableOpacity
                style={[styles.retryEmailBtn, { marginTop: 8 }, retryingEmailId === selectedEmail?.id && styles.retryEmailBtnDisabled]}
                onPress={() => handleRetryEmail(selectedEmail)}
                disabled={retryingEmailId === selectedEmail?.id}
              >
                {retryingEmailId === selectedEmail?.id ? (
                  <ActivityIndicator size="small" color="#fff" style={{ marginRight: 6 }} />
                ) : (
                  <FontAwesome5 name="redo" size={12} color="#fff" style={{ marginRight: 6 }} />
                )}
                <Text style={styles.retryEmailBtnText}>
                  {retryingEmailId === selectedEmail?.id ? 'Retrying…' : 'Retry send'}
                </Text>
              </TouchableOpacity>
            </View>

            {/* Body */}
            <View style={styles.modalBody}>
              {loadingEmailDetail ? (
                <View style={styles.emptyState}>
                  <ActivityIndicator size="large" color="#009999" />
                  <Text style={styles.emptyStateText}>Loading email content…</Text>
                </View>
              ) : (
                renderEmailHtmlBody()
              )}
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  const renderSubTabs = () => (
    <View style={styles.subTabBar}>
      <TouchableOpacity
        style={[styles.subTabItem, subTab === 'sms' && styles.subTabItemActive]}
        onPress={() => setSubTab('sms')}
      >
        <FontAwesome5 name="sms" size={14} color={subTab === 'sms' ? '#009999' : '#666'} style={{ marginRight: 6 }} />
        <Text style={[styles.subTabText, subTab === 'sms' && styles.subTabTextActive]}>SMS</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.subTabItem, subTab === 'email' && styles.subTabItemActive]}
        onPress={() => setSubTab('email')}
      >
        <FontAwesome5 name="envelope" size={14} color={subTab === 'email' ? '#009999' : '#666'} style={{ marginRight: 6 }} />
        <Text style={[styles.subTabText, subTab === 'email' && styles.subTabTextActive]}>Email</Text>
      </TouchableOpacity>
    </View>
  );

  const renderMessageTable = () => (
    <View style={styles.tableContainer}>
      {/* Table Header */}
      <View style={styles.tableHeader}>
        {/* <Text style={[styles.columnHeader, styles.directionHeader]}>Direction</Text>
        <Text style={[styles.columnHeader, styles.fromHeader]}>From</Text>
        <Text style={[styles.columnHeader, styles.toHeader]}>To</Text> */}
        <Text style={[styles.columnHeader, styles.messageHeader]}>Message</Text>
        <Text style={[styles.columnHeader, styles.photoHeader]}>Photos</Text>
        <Text style={[styles.columnHeader, styles.timeHeader]}>Date Sent</Text>
      </View>

      {/* Table Body */}
      {messages.length > 0 ? (
        <ScrollView style={styles.messageList} showsVerticalScrollIndicator={false}>
          {messages.map((item) => renderMessageRow({ item }))}
        </ScrollView>
      ) : (
        <View style={styles.emptyState}>
          <FontAwesome5 name="comments" size={48} color="#ccc" />
          <Text style={styles.emptyStateText}>No messages found</Text>
        </View>
      )}
    </View>
  );

  return (
    <View style={styles.container}>
      {renderSubTabs()}
      {subTab === 'sms'
        ? renderSection('Message History', renderMessageTable(), 'comments')
        : renderSection('Email Logs', (
            <>
              {renderEmailToolbar()}
              {renderEmailTable()}
            </>
          ), 'envelope')}
      {renderEmailModal()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#fff',
  },
  emailToolbar: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingHorizontal: 12,
    paddingBottom: 10,
  },
  sendConfirmationBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#009999',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 6,
  },
  sendConfirmationBtnDisabled: {
    opacity: 0.6,
  },
  sendConfirmationBtnText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 14,
  },
  section: {
    flex: 1,
    backgroundColor: 'white',
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  sectionIcon: {
    marginRight: 10,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
  },
  sectionContent: {
    flex: 1,
    paddingTop: 20,
  },
  // Message Table Styles - matching EquipmentsTable
  tableContainer: {
    flex: 1,
    marginBottom: 16,
    marginTop: 6,
    overflow: 'hidden',
    borderTopWidth: 1,
    borderTopColor: '#bfbfbf',
    borderLeftWidth: 1,
    borderLeftColor: '#bfbfbf',
    borderRightWidth: 1,
    borderRightColor: '#bfbfbf',
    borderBottomWidth: 1,
    borderBottomColor: '#bfbfbf',
  },
  tableHeader: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
    backgroundColor: '#f5f5f5',
  },
  columnHeader: {
    fontWeight: 'bold',
    alignItems: 'flex-start',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRightWidth: 1,
    borderRightColor: '#ccc',
    fontSize: TextdefaultSize,
  },
  directionHeader: {
    width: 120,
  },
  fromHeader: {
    width: 140,
  },
  toHeader: {
    width: 140,
  },
  messageHeader: {
    flex: 1,
    minWidth: 200,
  },
  photoHeader: {
    width: 90,
  },
  timeHeader: {
    width: 160,
    borderRightWidth: 0, // Remove right border for last column
  },
  messageList: {
    flex: 1,
  },
  messageRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
    minHeight: 50,
  },
  messageCell: {
    position: 'relative',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderRightWidth: 1,
    borderRightColor: '#ddd',
  },
  directionCell: {
    width: 104,
    alignItems: 'center',
  },
  fromCell: {
    width: 120,
  },
  toCell: {
    width: 120,
  },
  photoCell: {
    width: 90,
  },
  messageContentCell: {
    flex: 1,
    minWidth: 200,
    position: 'relative',
  },
  timeCell: {
    width: 160,
    borderRightWidth: 0, // Remove right border for last column
  },
  messageCellValue: {
    fontSize: TextdefaultSize,
    color: '#212529',
  },
  directionBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    alignSelf: 'center',
  },
  sentBadge: {
    backgroundColor: '#d1ecf1',
  },
  receivedBadge: {
    backgroundColor: '#d4edda',
  },
  directionText: {
    fontSize: 12,
    fontWeight: '500',
  },
  sentText: {
    color: '#0c5460',
  },
  receivedText: {
    color: '#155724',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  emptyStateText: {
    fontSize: 16,
    color: '#6c757d',
    marginTop: 12,
  },
  messageContentWrapper: {
    position: 'relative',
    width: '100%',
  },
  subTabBar: {
    flexDirection: 'row',
    gap: 8,
    paddingTop: 12,
    paddingHorizontal: 4,
  },
  subTabItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    backgroundColor: '#fafafa',
  },
  subTabItemActive: {
    borderColor: '#009999',
    backgroundColor: '#e6f7f7',
  },
  subTabText: {
    fontSize: TextdefaultSize,
    color: '#666',
  },
  subTabTextActive: {
    color: '#009999',
    fontWeight: '600',
  },
  emailRowWrapper: {
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
  },
  emailTypeCell: {
    width: 120,
    flexShrink: 0,
  },
  emailToCell: {
    width: 150,
    flexShrink: 0,
  },
  emailSubjectCell: {
    flex: 1,
    minWidth: 100,
    flexShrink: 1,
  },
  emailStatusCell: {
    width: 80,
    flexShrink: 0,
    alignItems: 'center',
  },
  emailDetail: {
    paddingHorizontal: 14,
    paddingVertical: 12,
    backgroundColor: '#f9f9f9',
  },
  emailDetailLabel: {
    fontSize: TextSmallSize,
    color: '#666',
    marginBottom: 6,
  },
  emailDetailValue: {
    color: '#212529',
    fontWeight: '400',
  },
  emailBodyText: {
    fontSize: TextdefaultSize,
    color: '#212529',
    marginTop: 4,
  },
  emailErrorText: {
    fontSize: TextSmallSize,
    color: '#dc3545',
    marginBottom: 6,
  },
  failedBadge: {
    backgroundColor: '#f8d7da',
  },
  failedText: {
    color: '#721c24',
  },
  copyButton: {
    position: 'absolute',
    right: 0,
    top: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    padding: 2,
    borderRadius: 0,
    zIndex: 10,
  },

  // ===== Email list (card layout) =====
  emailListContainer: {
    marginTop: 6,
    marginBottom: 16,
  },
  emailCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#e6e6e6',
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 14,
    marginBottom: 10,
    ...Platform.select({
      web: { boxShadow: '0 1px 3px rgba(0,0,0,0.06)' } as any,
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.06,
        shadowRadius: 3,
        elevation: 1,
      },
    }),
  },
  emailIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  emailIconWrapSent: {
    backgroundColor: '#e6f7f7',
  },
  emailIconWrapFailed: {
    backgroundColor: '#fdeaec',
  },
  emailCardBody: {
    flex: 1,
    minWidth: 0,
  },
  emailCardTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  emailSubject: {
    flex: 1,
    fontSize: TextMediumSize,
    fontWeight: '600',
    color: '#212529',
    marginRight: 8,
  },
  statusPill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
  },
  emailMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 2,
  },
  emailTypeChip: {
    backgroundColor: '#f0f0f0',
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    marginRight: 8,
  },
  emailTypeChipText: {
    fontSize: TextSmallSize,
    color: '#555',
    fontWeight: '500',
  },
  emailMetaText: {
    flex: 1,
    fontSize: TextSmallSize,
    color: '#444',
  },
  emailMetaTextMuted: {
    fontSize: TextSmallSize,
    color: '#888',
  },
  emailCardChevron: {
    paddingLeft: 10,
  },
  emailDateCell: {
    width: 110,
    flexShrink: 0,
    borderRightWidth: 0,
  },
  emailSubRowMeta: {
    fontSize: TextSmallSize,
    color: '#888',
    marginTop: 2,
  },
  emailActionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 10,
  },
  viewEmailBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#009999',
    backgroundColor: '#e6f7f7',
  },
  viewEmailBtnText: {
    fontSize: TextSmallSize,
    color: '#009999',
    fontWeight: '600',
  },
  retryEmailBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    backgroundColor: '#dc3545',
  },
  retryEmailBtnDisabled: {
    opacity: 0.6,
  },
  retryEmailBtnText: {
    fontSize: TextSmallSize,
    color: '#fff',
    fontWeight: '600',
  },

  // ===== Email detail modal =====
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  modalCard: {
    width: '100%',
    maxWidth: 720,
    height: '90%',
    backgroundColor: '#fff',
    borderRadius: 12,
    overflow: 'hidden',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 18,
    paddingTop: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  modalSubject: {
    fontSize: 17,
    fontWeight: '700',
    color: '#212529',
    marginBottom: 8,
  },
  modalMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  modalMetaText: {
    fontSize: TextSmallSize,
    color: '#666',
  },
  modalCloseBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f2f2f2',
  },
  modalInfoBar: {
    paddingHorizontal: 18,
    paddingVertical: 12,
    backgroundColor: '#fafafa',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  modalInfoLine: {
    fontSize: TextSmallSize,
    color: '#444',
    marginBottom: 3,
  },
  modalInfoKey: {
    color: '#888',
    fontWeight: '600',
  },
  modalBody: {
    flex: 1,
    backgroundColor: '#fff',
  },
  emailWebView: {
    flex: 1,
  },
  emailModalTextWrap: {
    flex: 1,
    padding: 16,
  },
});

export default CommunicationTab;